/**
 * ListView — the T1 List-View template chrome (UX-Inventar §4 T1): title + saved-view pills +
 * toolbar (search, save view, filter, "x von y") wrapping a DataTable (passed as children).
 * Every list screen = a config of this template.
 *
 * UI-U3 restyle (AC 3/9): H1 uses the display-title voice; saved-view pills are
 * UI-U1 FilterChips with inline counts (design.md §5); toolbar controls are 36px
 * / 6px-radius. No API change — screens keep passing the same props.
 */
import { useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { SearchBox } from './SearchBox';
import { Button } from '../ui/Button';
import { FilterChip } from '../ui/FilterChip';
import { StickyPageHeader } from '../ui/StickyPageHeader';

export interface ViewPill {
  label: string;
  count?: number;
}

interface ListViewProps {
  title: string;
  views: ViewPill[];
  count: string; // "12 von 12"
  searchPlaceholder?: string;
  rightAction?: ReactNode;
  /** Search box value (controlled — optional; omitted = uncontrolled/no-op). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /**
   * Rendered directly beside the search box (same toolbar row, right after it,
   * before the Filter button) — for a control that governs THIS search, e.g. a
   * Volltext/Semantisch SearchMode SegmentedControl. Distinct from `rightAction`
   * (title row, top-right): that slot is for page-level actions ("Neue Police",
   * view-mode toggle) unrelated to the search box itself, and sits far enough
   * away that a search-scoped control there reads as disconnected from search.
   * [ea/fix-search]
   */
  searchExtra?: ReactNode;
  /**
   * Rendered on its own row directly UNDER the toolbar, still inside the pinned
   * header - for a notice about the search that just ran, e.g. "the semantic leg is
   * degraded". Separate from `searchExtra` (a control beside the box) because a
   * notice is a full-width line, and separate from the table area because it must
   * stay visible while the rows scroll. [S4 follow-up]
   */
  searchNotice?: ReactNode;
  /**
   * Controlled active-view index (optional). When provided the pills are
   * controlled by the host (so a screen can actually FILTER on the preset);
   * omitted = internal uncontrolled state, unchanged for every existing consumer.
   * [Story UI-U4]
   */
  activeView?: number;
  onViewChange?: (index: number) => void;
  /**
   * Advanced-filter panel — the content the "Filter" button toggles open (on its own row
   * under the toolbar). When omitted the Filter button is NOT rendered at all (it used to be
   * a dead placeholder on every list screen). `activeFilterCount` shows a badge + keeps the
   * button highlighted while filters are applied, even with the panel collapsed.
   */
  filterPanel?: ReactNode;
  activeFilterCount?: number;
  children: ReactNode; // the DataTable
}

export function ListView({ title, views, count, searchPlaceholder, rightAction, searchValue, onSearchChange, searchExtra, searchNotice, activeView, onViewChange, filterPanel, activeFilterCount, children }: ListViewProps) {
  const [internalActive, setInternalActive] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const active = activeView ?? internalActive;
  const setActive = (i: number) => {
    if (activeView === undefined) setInternalActive(i);
    onViewChange?.(i);
  };

  return (
    <div className="px-6 py-5">
      {/*
        E3: one pinned block (title + pills + toolbar) for all five list screens.
        `-mx-6 px-6` reaches back over this screen's px-6 so the pinned block
        covers the page gutters; `-mt-5 pt-5` does the same for the top padding.
      */}
      <StickyPageHeader className="-mx-6 -mt-5 px-6 pt-5">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="display-title">{title}</h1>
        {rightAction}
      </div>

      {/* saved-view pills — FilterChips (design.md §5): active = lavender/navy. The pills are
          host-supplied presets; there is no user-saved-view feature, so the old "Ansicht
          speichern" placeholder (a dead button on every list screen) is not rendered — same
          call as the Filter button, which only shows once it has a panel to open. */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {views.map((v, i) => (
          <FilterChip
            key={v.label}
            label={v.label}
            count={v.count}
            active={active === i}
            onClick={() => setActive(i)}
          />
        ))}
      </div>

      {/* toolbar — 36px controls, 6px radius */}
      <div className="mb-2 flex items-center gap-2">
        <SearchBox value={searchValue} onChange={onSearchChange} placeholder={searchPlaceholder} />
        {searchExtra}
        {filterPanel && (
          <Button
            variant={filterOpen || (activeFilterCount ?? 0) > 0 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterOpen((o) => !o)}
            aria-expanded={filterOpen}
          >
            <SlidersHorizontal className="size-3.5" /> Filter
            {(activeFilterCount ?? 0) > 0 && (
              <span className="ml-1 rounded-full bg-background/25 px-1.5 text-[11px] leading-tight">
                {activeFilterCount}
              </span>
            )}
          </Button>
        )}
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">{count}</span>
      </div>

      {filterPanel && filterOpen && (
        <div className="mb-2 rounded-md border bg-muted/30 p-3">{filterPanel}</div>
      )}

      {searchNotice}
      </StickyPageHeader>

      {children}
    </div>
  );
}
